import React, { useEffect, useMemo, useRef, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import logo from "../assets/logo.png";

const AGREEMENT_VERSION = "matrixos-trial-v3";
const REGION = "us-central1";

const TERMS = [
  { title: "1. Introduction", body: "These terms govern trial, pilot, or demo use of MatrixOS platforms, including Neo, Morpheus, Link, and any supporting services enabled for the client." },
  { title: "2. Trial Period", body: "A trial period may be granted from the activation date unless otherwise agreed in writing by MatrixOS. Trial limits may apply to sites, stores, users, modules, integrations, support, or usage volume. Trial usage is not billed unless otherwise agreed." },
  { title: "3. Usage", body: "The client agrees to use MatrixOS properly, responsibly, and lawfully, and not to misuse access, data, devices, integrations, reports, or platform functionality." },
  { title: "4. Transition to Live Usage", body: "After the trial period, the client may choose which sites, stores, modules, or users continue into live use. Activating live usage may create billing obligations according to the accepted commercial terms." },
  { title: "5. Billing After Trial", body: "If the client continues using MatrixOS after the trial, billing may be calculated using enabled modules, active sites, stores, active staff, live sessions, fixed monthly charges, or other agreed pricing." },
  { title: "6. Payment Terms", body: "Payment is due according to the accepted invoice, checkout, proposal, order form, or signed agreement. MatrixOS may restrict or suspend services if payment is not received." },
  { title: "7. Termination", body: "Either party may terminate the trial at any time. If the client continues into live usage and later terminates, any usage or agreed charges already incurred remain payable." },
  { title: "8. Data & Ownership", body: "The client retains ownership of its business data. MatrixOS retains ownership of the platform, software, workflows, branding, dashboards, reports, and intellectual property." },
  { title: "9. No Warranty", body: "Trial access is provided as is, without warranties of uninterrupted service, suitability, or fitness for a specific business outcome." },
  { title: "10. Liability", body: "MatrixOS is not liable for losses, downtime, delays, incorrect source data, stock losses, delivery delays, or damages arising from trial use, except where liability cannot legally be excluded." },
  { title: "11. Confidentiality and POPIA", body: "The client agrees to protect confidential MatrixOS information and confirms it has authority to provide any personal information entered into the platform. Personal information must be handled in accordance with applicable law, including POPIA where relevant." },
  { title: "12. Governing Law", body: "This agreement is governed by the laws of the Republic of South Africa." },
  { title: "13. Acceptance", body: "By signing electronically below, the client confirms that they have read, understood, and accepted these terms and conditions." },
];

function Toast({ open, title, message, tone = "success", onClose }) {
  if (!open) return null;

  const toneClasses =
    tone === "success"
      ? "border-cyan-400/45 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.22),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(34,211,238,0.12),0_18px_48px_rgba(6,182,212,0.28)]"
      : "border-rose-400/45 bg-[radial-gradient(circle_at_top,rgba(244,63,94,0.18),rgba(15,23,42,0.96))] shadow-[0_0_0_1px_rgba(244,63,94,0.12),0_18px_48px_rgba(244,63,94,0.18)]";

  return (
    <div className="fixed right-4 top-24 z-[9999] max-w-[calc(100vw-2rem)]">
      <div className={`w-[380px] rounded-2xl border p-4 backdrop-blur-xl ${toneClasses}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-100/90">{message}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-300 hover:text-white">✕</button>
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = Math.max(wrap.clientWidth, 320);
    const height = 190;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = value;
    } else {
      ctx.clearRect(0, 0, width, height);
    }
  }, [value]);

  useEffect(() => {
    const onResize = () => onChange((prev) => prev || "");
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [onChange]);

  const pointFromEvent = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    return {
      x: source.clientX - rect.left,
      y: source.clientY - rect.top,
    };
  };

  const start = (event) => {
    event.preventDefault();
    drawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = pointFromEvent(event);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const p = pointFromEvent(event);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const canvas = canvasRef.current;
    onChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className="space-y-3">
      <div ref={wrapRef} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <canvas
          ref={canvasRef}
          className="block w-full touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Draw signature using finger, stylus, or mouse.</p>
        <button type="button" onClick={clear} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          Clear signature
        </button>
      </div>
    </div>
  );
}

export default function TrialAgreementPage() {
  const fns = useMemo(() => getFunctions(undefined, REGION), []);
  const [form, setForm] = useState({
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    notes: "",
    acceptedTerms: false,
    acceptedElectronic: false,
  });
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteState, setInviteState] = useState({ valid: false, reason: "", expiresAtLabel: "" });
  const [signed, setSigned] = useState(false);
  const [token, setToken] = useState("");
  const [toast, setToast] = useState({ open: false, title: "", message: "", tone: "success" });
  const [redirecting, setRedirecting] = useState(false);

  const canSubmit = useMemo(() => {
    return (
      inviteState.valid &&
      form.fullName.trim() &&
      form.email.trim() &&
      form.acceptedTerms &&
      form.acceptedElectronic &&
      !!signature &&
      !submitting &&
      !signed
    );
  }, [inviteState.valid, form, signature, submitting, signed]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    let alive = true;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const secureToken = String(params.get("token") || "").trim();
      setToken(secureToken);

      if (!secureToken) {
        setInviteState({ valid: false, reason: "missing", expiresAtLabel: "" });
        setLoadingInvite(false);
        return;
      }

      try {
        const getInvite = httpsCallable(fns, "getTrialAgreementInvite");
        const res = await getInvite({ token: secureToken });
        const payload = res?.data || {};
        if (!alive) return;

        setForm((prev) => ({
          ...prev,
          fullName: payload.fullName || "",
          companyName: payload.companyName || "",
          email: payload.email || "",
        }));
        setInviteState({
          valid: true,
          reason: "",
          expiresAtLabel: payload.expiresAtLabel || "",
        });
      } catch (error) {
        if (!alive) return;
        const message = String(error?.message || "").toLowerCase();
        let reason = "invalid";
        if (message.includes("expired")) reason = "expired";
        if (message.includes("used")) reason = "used";
        setInviteState({ valid: false, reason, expiresAtLabel: "" });
      } finally {
        if (alive) setLoadingInvite(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [fns]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setToast({
        open: true,
        title: "Missing information",
        message: "Please complete the required fields, accept all terms, and add the client signature.",
        tone: "error",
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      setSubmitting(true);
      const submitAgreement = httpsCallable(fns, "submitTrialAgreement");
      await submitAgreement({
        token,
        phone: form.phone.trim(),
        notes: form.notes.trim(),
        acceptedTerms: true,
        acceptedElectronic: true,
        signatureDataUrl: signature,
        pagePath: typeof window !== "undefined" ? window.location.pathname : "/trial-agreement",
      });

      setSigned(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setToast({
        open: true,
        title: "Agreement signed",
        message: "A signed copy will be emailed now. Please also check your email for the password setup link. Redirecting to login...",
        tone: "success",
      });
    } catch (error) {
      console.error("[TrialAgreementPage] secure submit failed", error);
      const message = String(error?.message || "").toLowerCase();
      let toastMessage = "Something went wrong while submitting the agreement.";
      if (message.includes("expired")) toastMessage = "This secure agreement link has expired.";
      if (message.includes("used")) toastMessage = "This agreement link has already been used.";
      if (message.includes("invalid")) toastMessage = "This secure agreement link is invalid.";
      setToast({
        open: true,
        title: "Could not submit",
        message: toastMessage,
        tone: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };


  useEffect(() => {
    if (!signed) return;
    setRedirecting(true);
    const timer = window.setTimeout(() => {
      window.location.assign("/login");
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [signed]);

  const invalidTitle =
    inviteState.reason === "expired"
      ? "Agreement link expired"
      : inviteState.reason === "used"
      ? "Agreement already signed"
      : "Secure link required";

  const invalidMessage =
    inviteState.reason === "expired"
      ? "This secure trial agreement link has expired. Please request a new link from MatrixOS."
      : inviteState.reason === "used"
      ? "This secure trial agreement link has already been used to sign the agreement."
      : "This page now requires a valid secure token. Please open the agreement using the secure link generated by MatrixOS.";

  return (
    <div className="min-h-screen bg-[#071120] text-white">
      <SiteHeader />
      <Toast
        open={toast.open}
        title={toast.title}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast({ open: false, title: "", message: "", tone: "success" })}
      />

      <main className="mx-auto max-w-7xl px-6 pb-20 pt-32">
        <section className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr]">
          <div className="space-y-8">
            <div className="rounded-[2rem] border border-cyan-300/12 bg-[linear-gradient(180deg,rgba(19,49,90,0.56),rgba(8,20,37,0.96))] p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-cyan-400/10 ring-1 ring-cyan-300/20 shadow-[0_0_32px_rgba(34,211,238,0.24)]">
                    <img src={logo} alt="MatrixOS logo" className="h-16 w-16 object-contain" />
                  </div>
                  <div>
                    <div className="mb-2 inline-flex rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-cyan-200">
                      Trial agreement
                    </div>
                    <h1 className="text-4xl font-extrabold leading-tight text-white md:text-5xl">MatrixOS</h1>
                    <p className="mt-2 text-xl text-cyan-300">Operations intelligence</p>
                  </div>
                </div>

                <div className="grid gap-2 text-sm text-slate-300 sm:text-right">
                  <div>Neo Platform</div>
                  <div>Secure token access</div>
                  <div>1 store free for 30 days</div>
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-cyan-300/12 bg-[linear-gradient(180deg,rgba(18,36,60,0.96),rgba(8,20,37,0.98))] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.35)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.28em] text-cyan-300">Trial terms & conditions</div>
                  <h2 className="mt-3 text-3xl font-extrabold text-white">Please review before signing</h2>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
                  Version: <span className="font-semibold text-white">{AGREEMENT_VERSION}</span>
                </div>
              </div>

              <div className="mt-6 max-h-[720px] space-y-4 overflow-y-auto pr-1">
                {TERMS.map((item) => (
                  <div key={item.title} className="rounded-2xl border border-white/8 bg-white/[0.03] p-5">
                    <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                    <p className="mt-2 leading-7 text-slate-300">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {loadingInvite ? (
            <div className="self-start rounded-[2rem] border border-cyan-300/12 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
              <div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Client acceptance</div>
              <h2 className="mt-3 text-3xl font-extrabold">Loading secure agreement…</h2>
              <p className="mt-4 leading-7 text-slate-600">Please wait while we validate your secure access link.</p>
            </div>
          ) : !inviteState.valid ? (
            <div className="self-start rounded-[2rem] border border-rose-300/20 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
              <div className="text-xs uppercase tracking-[0.28em] text-rose-700">Trial agreement</div>
              <h2 className="mt-3 text-3xl font-extrabold">{invalidTitle}</h2>
              <p className="mt-4 leading-7 text-slate-600">{invalidMessage}</p>
            </div>
          ) : (
            <div className="self-start rounded-[2rem] border border-cyan-300/12 bg-white p-8 text-slate-900 shadow-[0_30px_80px_rgba(0,0,0,0.35)] md:p-10">
              <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Client acceptance</div>
                <h2 className="mt-3 text-3xl font-extrabold">Accept and sign</h2>
                <p className="mt-3 leading-7 text-slate-600">
                  This secure agreement link is valid{inviteState.expiresAtLabel ? ` until ${inviteState.expiresAtLabel}` : ""}. Review the terms, accept them, and sign digitally. Trial access covers one store only, and trial usage is not billed.
                </p>
              </div>

              {signed && (
                <div className="mb-5 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-4 text-sm text-cyan-900">
                  Your signed copy will be emailed to you, and a password setup email has been sent for login access. You will be redirected to <strong>/login</strong> in about 10 seconds.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <Field label="Client full name *">
                  <input className={inputCls} value={form.fullName} readOnly disabled />
                </Field>

                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Company name">
                    <input className={inputCls} value={form.companyName} readOnly disabled />
                  </Field>
                  <Field label="Client email *">
                    <input type="email" className={inputCls} value={form.email} readOnly disabled />
                  </Field>
                </div>

                <Field label="Phone / WhatsApp">
                  <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Optional" />
                </Field>

                <Field label="Notes">
                  <textarea className={`${inputCls} min-h-[110px]`} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional onboarding notes" />
                </Field>

                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700">
                  <input type="checkbox" checked={form.acceptedTerms} onChange={(e) => set("acceptedTerms", e.target.checked)} className="mt-1" disabled={signed} />
                  <span>I confirm that I have read and accept all MatrixOS trial terms and conditions.</span>
                </label>

                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700">
                  <input type="checkbox" checked={form.acceptedElectronic} onChange={(e) => set("acceptedElectronic", e.target.checked)} className="mt-1" disabled={signed} />
                  <span>I consent to electronic signature and electronic delivery of the signed agreement.</span>
                </label>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-700">Client signature *</div>
                  <SignaturePad value={signature} onChange={setSignature} disabled={signed} />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full rounded-full bg-cyan-400 px-6 py-3 font-semibold text-[#071120] transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Submitting agreement..." : signed ? (redirecting ? "Redirecting to login..." : "Agreement signed") : "Accept & Sign"}
                </button>
              </form>
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
const inputCls = "mt-2 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 disabled:cursor-not-allowed disabled:text-slate-500";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}
